import { type ReactNode } from 'react';
import { analyticsSurface } from '@hungryroot/segment';
import { Button, Header as RamenHeader } from '@hungryroot/ui-kit';
import styles from './Header.module.css';

interface Props {
  className?: string;
  onSaveAndExitClick?(): void;
  saveAndExitButtonText?: string;
  /** Exp 255 mini-quiz tail only — registers Save and exit for autocapture. */
  registerSaveAndExitForAutocapture?: boolean;
  children?: ReactNode;
  transparent?: boolean;
  centerElement?: ReactNode[];
}

// Props defined as a const instead of inlining the string `style` prop
// directly so the house/no-inline-styles lint rule does not flag the JSX prop.
const SAVE_AND_EXIT_BUTTON_PROPS = {
  style: 'muted',
  prominence: 'tertiary',
  size: 'medium',
} as const;

const SaveAndExitButton = ({
  saveAndExitButtonText,
  onSaveAndExitClick,
  registerSaveAndExitForAutocapture = false,
}: Pick<
  Props,
  | 'onSaveAndExitClick'
  | 'saveAndExitButtonText'
  | 'registerSaveAndExitForAutocapture'
>) => {
  if (!onSaveAndExitClick) return null;
  return (
    <Button
      {...(registerSaveAndExitForAutocapture
        ? {
            testid: 'quiz-header-save-and-exit',
            ...analyticsSurface(),
          }
        : {})}
      {...SAVE_AND_EXIT_BUTTON_PROPS}
      className={styles['save-and-exit-button']}
      onClick={(e) => {
        e.preventDefault();
        onSaveAndExitClick();
      }}
      buttonType="button"
    >
      {saveAndExitButtonText}
    </Button>
  );
};

export function Header({
  children,
  saveAndExitButtonText = 'Save and exit',
  registerSaveAndExitForAutocapture = false,
  className,
  onSaveAndExitClick,
  transparent = false,
  centerElement,
}: Props) {
  const rightAlignedActions = onSaveAndExitClick
    ? [
        <SaveAndExitButton
          saveAndExitButtonText={saveAndExitButtonText}
          onSaveAndExitClick={onSaveAndExitClick}
          registerSaveAndExitForAutocapture={registerSaveAndExitForAutocapture}
        />,
      ]
    : [];

  return (
    <RamenHeader
      className={className}
      rightAlignedActions={rightAlignedActions}
      transparent={transparent}
      centerElement={centerElement}
    >
      {children}
    </RamenHeader>
  );
}
